import { Folder as FolderIcon } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, IconButton, Typography } from '@mui/material';
import { useState } from 'react';
import { FolderItem, GraphFolderStructure } from '../../../../../types/graph';
import FileTypeIcon from '../FileSelector/FileTypeIcon';
import React from 'react';

interface CustomerFolderStructureProps {
	item: GraphFolderStructure;
	onFileSelect?: (file: FolderItem) => void;
	expandFirstLevel?: boolean;
}

export const CustomerFolderStructure: React.FC<CustomerFolderStructureProps> = ({ item, onFileSelect, expandFirstLevel = false }) => {
	const [expandedFolders, setExpandedFolders] = useState<Set<string>>(() => {
		if (expandFirstLevel && item.id) {
			return new Set([item.id]);
		}
		return new Set();
	});

	const toggleFolder = (folderId: string) => {
		setExpandedFolders(prev => {
			const newSet = new Set(prev);
			if (newSet.has(folderId)) {
				newSet.delete(folderId);
			} else {
				newSet.add(folderId);
			}
			return newSet;
		});
	};

	const renderTree = (node: GraphFolderStructure) => {
		const isExpanded = expandedFolders.has(node.id);
		const hasChildren = node.type === 'folder' && node.children && node.children.length > 0;

		return (
			<Box key={node.id}>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 1,
						p: 0.5,
						'&:hover': {
							bgcolor: 'action.hover',
						},
					}}
				>
					{hasChildren && (
						<IconButton
							size='small'
							onClick={e => {
								e.stopPropagation();
								toggleFolder(node.id);
							}}
							sx={{ p: 0.5 }}
						>
							{isExpanded ? <ExpandMoreIcon sx={{ fontSize: '1.2rem' }} /> : <ChevronRightIcon sx={{ fontSize: '1.2rem' }} />}
						</IconButton>
					)}
					{node.type === 'folder' ? (
						<FolderIcon
							color='primary'
							sx={{
								fontSize: '1.2rem',
							}}
						/>
					) : (
						<FileTypeIcon fileName={node.name} />
					)}
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
						{node.type === 'file' ? (
							<>
								<Typography variant='body2'>
									<a
										href={node.webUrl}
										target='_blank'
										rel='noopener noreferrer'
										style={{ textDecoration: 'none', color: 'inherit' }}
									>
										{node.name}
									</a>
								</Typography>
								{onFileSelect && (
									<IconButton
										size='small'
										onClick={e => {
											e.stopPropagation();
											onFileSelect(node);
										}}
									>
										<AddIcon fontSize='small' />
									</IconButton>
								)}
							</>
						) : (
							<Typography variant='body2'>{node.name}</Typography>
						)}
					</Box>
				</Box>
				{hasChildren && isExpanded && (
					<Box sx={{ ml: 2, borderLeft: 1, borderColor: 'divider', pl: 1 }}>
						{node?.children?.map(child => renderTree(child))}
					</Box>
				)}
			</Box>
		);
	};

	return renderTree(item);
};
